import * as React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import { createBrowserRouter, createRoutesFromElements, Navigate, Route, RouterProvider } from "react-router-dom";
import AdminLayout from "./layouts/adminLayout";
import AdminTrendsPage from "./pages/dashboards/admin-trends";
import PayoutsPage from "./pages/dashboards/payouts";
import ExpensesPage from "./pages/database/apartment-expenses";
import BlockedCalendarPage from "./pages/database/blocked";
import LoginPage from "./pages/login";
import GroupedExpenses from "./pages/dashboards/group-expenses";
import { GroupGraphsPage } from "./pages/graphs/group-graphs";
import { AuthLayout } from "./pages/authLayout";
import ReportsPage from "./pages/reports";
import UserLayout from "./layouts/userLayout";
import { AdminReservationsPage } from "./pages/database/adminReservationsPage";
import { UserReservationsPage } from "./pages/userReservationsPage";
import AdminGroupedExpensesPage from "./pages/dashboards/admin-group-expenses";
import UsersPage from "./pages/usersPage";
import UserTrends from "./pages/userTrends";
import PayoutRatiosPage from "./pages/database/payout-ratios-page";
import ManagementFeeRatiosPage from "./pages/database/managementFeeRatiosPage";
import CleaningRatesPage from "./pages/database/cleaning-rates-page";
import BusinessFeesPage from "./pages/database/business-fees-page";
import RefileReviewPage from "./pages/database/refile-review-page";
import SyncIssuesPage from "./pages/database/sync-issues-page";
import ReconciliationPage from "./pages/database/reconciliation-page";
import HostHubMappingStatusPage from "./pages/database/hosthub-mapping-status-page";
import { PrivacyPolicy } from "./pages/privacyPolicy";
import { OauthHomePage } from "./pages/oauthHomePage";
import BookingPacePage from "./pages/dashboards/booking-pace";
import DailyDigestPage from "./pages/dashboards/daily-digest";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { OccupanciesPage } from "./pages/graphs/occupanciesPage";
import { ApartmentGraphsPage } from "@/pages/graphs/apartment-graphs";
import BltPage from "@/pages/dashboards/blt";
import OwnerBookingPacePage from "@/pages/dashboards/owner-booking-pace";
import OwnerOccupanciesPage from "@/pages/graphs/owner-occupancies";
import OwnerBltPage from "@/pages/dashboards/owner-blt";
import {
    CellStyleModule,
    ClientSideRowModelModule,
    ColumnApiModule,
    DateFilterModule,
    ModuleRegistry,
    NumberEditorModule,
    NumberFilterModule,
    PaginationModule,
    QuickFilterModule,
    RowAutoHeightModule,
    RowSelectionModule,
    RowStyleModule,
    TextEditorModule,
    TextFilterModule,
} from "ag-grid-community";
import {
    ColumnsToolPanelModule,
    ExcelExportModule,
    FiltersToolPanelModule,
    LicenseManager,
    PivotModule,
    RowGroupingModule,
    SetFilterModule,
} from "ag-grid-enterprise";
import { ApartmentsPage } from "./pages/apartments";
import ApartmentDetailsPage from "./pages/apartment-details";
import { OwnersPage } from "./pages/apartment-owners";
import OverviewPageNew from "@/pages/overview";
import PerformancePerUnitPage from "@/pages/dashboards/performance";
import NewApartmentPage from "@/pages/new-apartment";
import { ProtectedRoute, RootRedirect } from "@/protectedRoute";
import AirbnbReviewsPage from "@/pages/database/airbnb-reviews";
import TagsManagementPage from "@/pages/database/tagsManagementPage";
import MunicipalitySectionsManagementPage from "@/pages/database/municipalitySectionsManagementPage";
import AddressesManagementPage from "@/pages/database/addressesManagementPage";
import OwnerGroupsManagementPage from "@/pages/database/ownerGroupsManagementPage";
import ReviewsGraphsPage from "@/pages/graphs/reviews-graphs";
import SettingsPage from "@/pages/settings";
import FilingCredentialsPage from "@/pages/filing-credentials";
import { DemoAuthLayout } from "@/pages/demoAuthLayout";
import DemoLayout from "@/layouts/demoLayout";
import CalendarPage from "@/pages/owner-calendar";
import PayoutMechanismPage from "@/pages/demo/payout-mechanism";

const agGridLicenseKey = import.meta.env.VITE_AG_GRID_LICENSE_KEY;

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const router = createBrowserRouter(
    createRoutesFromElements(
        <>
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/logout" element={<LoginPage logoutAndClearSessions={true} />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/oauth-home" element={<OauthHomePage />} />

                {/* Root redirect */}
                <Route path="/" element={<RootRedirect />} />

                {/* Admin routes */}
                <Route
                    path="/admin"
                    element={
                        <ProtectedRoute requireAdmin>
                            <AdminLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<OverviewPageNew />} />
                    <Route path="daily-digest" element={<DailyDigestPage />} />
                    <Route path="apartments" element={<ApartmentsPage />} />
                    <Route path="owners" element={<OwnersPage />} />
                    <Route path="apartments/:id" element={<ApartmentDetailsPage />} />
                    <Route path="apartments/new" element={<NewApartmentPage />} />
                    <Route path="reservations" element={<AdminReservationsPage />} />
                    <Route path="refile-review" element={<RefileReviewPage />} />
                    <Route path="sync-issues" element={<SyncIssuesPage />} />
                    <Route path="reconciliation" element={<ReconciliationPage />} />
                    <Route path="hosthub-mapping" element={<HostHubMappingStatusPage />} />
                    <Route path="payout-ratios" element={<PayoutRatiosPage />} />
                    <Route path="management-fee-ratios" element={<ManagementFeeRatiosPage />} />
                    <Route path="cleaning-rates" element={<CleaningRatesPage />} />
                    <Route path="business-fees" element={<BusinessFeesPage />} />
                    <Route path="trends" element={<AdminTrendsPage />} />
                    <Route path="payouts" element={<PayoutsPage />} />
                    <Route path="expenses-by-category" element={<AdminGroupedExpensesPage />} />
                    <Route path="expenses" element={<ExpensesPage />} />
                    <Route path="graphs/groups" element={<GroupGraphsPage />} />
                    <Route path="graphs/apartments" element={<ApartmentGraphsPage />} />
                    <Route path="occupancies" element={<OccupanciesPage />} />
                    <Route path="blocked" element={<BlockedCalendarPage />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="pace" element={<BookingPacePage />} />
                    <Route path="performance" element={<PerformancePerUnitPage />} />
                    <Route path="blt-breakdown" element={<BltPage />} />
                    <Route path="airbnb-reviews" element={<AirbnbReviewsPage />} />
                    <Route path="tags" element={<TagsManagementPage />} />
                    <Route
                        path="municipality-sections"
                        element={<MunicipalitySectionsManagementPage />}
                    />
                    <Route path="addresses" element={<AddressesManagementPage />} />
                    <Route path="owner-groups" element={<OwnerGroupsManagementPage />} />
                    <Route path="graphs/reviews" element={<ReviewsGraphsPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="filing-credentials" element={<FilingCredentialsPage />} />
                </Route>

                {/* User routes */}
                <Route
                    element={
                        <ProtectedRoute>
                            <UserLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="reservations" element={<UserReservationsPage />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="expenses" element={<ExpensesPage />} />
                    <Route path="trends" element={<UserTrends />} />
                    <Route path="aggregate-expenses" element={<GroupedExpenses />} />
                    <Route path="graphs/apartments" element={<ApartmentGraphsPage />} />
                    <Route path="owner-booking-pace" element={<OwnerBookingPacePage />} />
                    <Route path="owner-occupancies" element={<OwnerOccupanciesPage />} />
                    <Route path="owner-blt" element={<OwnerBltPage />} />
                </Route>
            </Route>

            {/* Demo routes - outside AuthLayout, no login required */}
            <Route path="/demo" element={<DemoAuthLayout />}>
                <Route element={<DemoLayout />}>
                    <Route index element={<Navigate to="/demo/reports" replace />} />
                    <Route path="reports" element={<ReportsPage />} />
                    <Route path="reservations" element={<UserReservationsPage />} />
                    <Route path="calendar" element={<CalendarPage />} />
                    <Route path="expenses" element={<ExpensesPage />} />
                    <Route path="trends" element={<UserTrends />} />
                    <Route path="aggregate-expenses" element={<GroupedExpenses />} />
                    <Route path="graphs/apartments" element={<ApartmentGraphsPage />} />
                    <Route path="owner-booking-pace" element={<OwnerBookingPacePage />} />
                    <Route path="owner-occupancies" element={<OwnerOccupanciesPage />} />
                    <Route path="owner-blt" element={<OwnerBltPage />} />
                    <Route path="payout-mechanism" element={<PayoutMechanismPage />} />
                </Route>
            </Route>
        </>,
    ),
);

ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    CellStyleModule,
    ColumnsToolPanelModule,
    ColumnApiModule,
    DateFilterModule,
    RowGroupingModule,
    PivotModule,
    RowAutoHeightModule,
    NumberEditorModule,
    NumberFilterModule,
    TextEditorModule,
    TextFilterModule,
    PaginationModule,
    QuickFilterModule,
    RowSelectionModule,
    RowStyleModule,
    FiltersToolPanelModule,
    SetFilterModule,
    ExcelExportModule,
]);

LicenseManager.setLicenseKey(agGridLicenseKey);

root.render(
    <React.StrictMode>
        <Provider store={store}>
            <RouterProvider router={router} />
        </Provider>
    </React.StrictMode>,
);
